import * as React from 'react';
import { connect } from 'react-redux';
import { History } from 'history';
import { Button, Card, CardBody, CardFooter, CardHeader } from 'reactstrap';
import makeComponentTrashable, { TrashableProps } from 'trashable-react';
import { TICKET_ACTIONS } from '../../actions/ticket';
import { apiRequest } from '../../APIClient';
import { APIError } from '../../APIError';
import { getObjVal, getOrigin, ROUTE_PATH } from '../../constants';
import { sessionSelector } from '../../reducers/session';
import { Dispatch, OBTeamContacts, TeamMemberType } from '../../types';
import ErrorJumbotron from '../../widgets/ErrorJumbotron';
import Icon from '../../widgets/Icons';
import Panel from '../../widgets/Panel';
import SendTicketButton from '../../widgets/SendTicketButton';
import ViewHeader from '../../widgets/ViewHeader';
import Viewport from '../../widgets/Viewport';
import './Contact.scss';
import MessageForm, { MessageBody, MessageFormStatus } from './MessageForm';
import { PageTitle } from '../../widgets/PageTitle';
import { onboardingSelector } from '../../reducers/onboarding';
import { TicketDetail } from '../../types/tickets';

type OwnProps = {
  history: History;
  initialSubject?: string;
  link?: string;
  showOnboarding?: boolean;
};

export type StoreProps = {
  cst: Array<TeamMemberType>;
  onboardingTeamContacts: OBTeamContacts;
};

export type DispatchProps = {
  setTicketFilters: (arg0: URLSearchParams) => void;
  onCreateTicket: (
    arg0: MessageBody,
    showOnboarding?: boolean,
    assigneeEmail?: string,
    emailCCs?: string[],
    onboardingForm?: boolean,
    pmTag?: string,
    pseTag?: string,
  ) => Promise<TicketDetail>;
  fetchOnboardingTeamContacts: () => void;
};

export type Props = OwnProps & StoreProps & DispatchProps & TrashableProps;

type State = {
  message: MessageBody;
  sending: boolean;
  sentTicketId: number | null;
  uploadInProgress: boolean;
};

export const ONBOARDING_DISPATCH_TICKET_TYPE = 'onboarding__dispatch';

export const TICKET_ERROR = -1;

export class BareContact extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      message: {
        attachmentTokens: [],
        description: '',
        link: props.link,
        subject: props.initialSubject || '',
      },
      sentTicketId: null,
      sending: false,
      uploadInProgress: false,
    };
    if (props.showOnboarding) {
      props.fetchOnboardingTeamContacts();
    }
  }

  onSend = async () => {
    const {
      registerPromise,
      onCreateTicket,
      showOnboarding,
      onboardingTeamContacts,
    } = this.props;
    this.setState({ sending: true });
    try {
      const assigneeEmail = showOnboarding
        ? onboardingTeamContacts?.pm?.email
        : undefined;
      const emailCCs =
        showOnboarding && onboardingTeamContacts?.pse?.email
          ? [onboardingTeamContacts.pse.email]
          : undefined;
      const pmTag =
        (showOnboarding &&
          onboardingTeamContacts?.pm?.first_name.toLowerCase() +
            '_' +
            onboardingTeamContacts?.pm?.last_name.toLowerCase()) ||
        undefined;
      const pseTag =
        (showOnboarding &&
          onboardingTeamContacts?.pse?.first_name.toLowerCase() +
            '_' +
            onboardingTeamContacts?.pse?.last_name.toLowerCase()) ||
        undefined;
      const ticket = await registerPromise(
        onCreateTicket(
          this.state.message,
          showOnboarding,
          assigneeEmail,
          emailCCs,
          showOnboarding,
          pmTag,
          pseTag,
        ),
      );
      this.setState({
        message: {
          subject: '',
          description: '',
          link: null,
          attachmentTokens: [],
        },
        sending: false,
        sentTicketId: getObjVal(ticket, 'id', ''),
      });
    } catch (err) {
      this.setState({
        sending: false,
        sentTicketId: TICKET_ERROR,
      });
    }
  };

  onMessageFormChanged = (status: MessageFormStatus) => {
    this.setState(status);
  };

  goToTicket = () => {
    this.props.history.push(
      `${ROUTE_PATH.TICKETS}/${this.state.sentTicketId || ''}`,
    );
  };

  goBack = () => {
    this.props.history.goBack();
  };

  clearResult = () => {
    this.setState({ sentTicketId: null });
  };

  render() {
    const { link, cst, history, onboardingTeamContacts, showOnboarding } =
      this.props;
    const { message, sending, sentTicketId, uploadInProgress } = this.state;
    let members: string[] = [];
    if (showOnboarding) {
      if (onboardingTeamContacts?.pm?.first_name) {
        members.push(
          onboardingTeamContacts.pm.first_name +
            ' ' +
            onboardingTeamContacts.pm.last_name,
        );
      }
      if (onboardingTeamContacts?.pse?.first_name) {
        members.push(
          onboardingTeamContacts.pse.first_name +
            ' ' +
            onboardingTeamContacts.pse.last_name,
        );
      }
    } else {
      // CSEs should be listed first
      const CSEs = cst.filter((member) => member.role === 'cse');
      const nonCSEs = cst.filter((member) => member.role !== 'cse');
      members = CSEs.concat(nonCSEs).map((member) => member.name);
    }
    const title = members.length
      ? showOnboarding
        ? 'Your Onboarding Team'
        : 'Your Concierge Security Team'
      : 'Arctic Wolf Networks';

    const contactAWN = (
      <div>
        <h5 className="title">{title}</h5>
        {members.map((member) => (
          <p className="name" key={member}>
            {member}
          </p>
        ))}
      </div>
    );
    return (
      <Viewport>
        <PageTitle title="Contact" />
        <ViewHeader title="Contact" />
        <div className="contact-split-panel">
          <Panel className="w-100 mb-prs">
            <h2 className="mb-4">
              {showOnboarding
                ? 'Contact your Onboarding Team'
                : 'Contact your Concierge Security Team'}
            </h2>
            {sentTicketId ? (
              sentTicketId === TICKET_ERROR ? (
                <ErrorJumbotron
                  className="mt-5"
                  error={
                    new APIError(
                      'There was a problem posting your ticket.  Please call your Concierge Security Team at 888-272-8429 or email security@arcticwolf.com',
                      500,
                    )
                  }
                />
              ) : (
                <Card className="result-card">
                  <CardHeader>
                    <Icon.Check size={50} />
                  </CardHeader>
                  <CardBody>
                    <h1>Success</h1>
                    <p>
                      Your ticket was successfully created. A new ticket #
                      {`${sentTicketId} `}
                      has been sent to your{' '}
                      {showOnboarding
                        ? 'Onboarding Team'
                        : 'Concierge Security Team'}
                      .
                    </p>
                  </CardBody>
                  <CardFooter>
                    <Button id="go" color="primary" onClick={this.goToTicket}>
                      View Ticket #{sentTicketId}
                    </Button>
                    <Button id="new" color="primary" onClick={this.clearResult}>
                      New Ticket
                    </Button>
                    {link && (
                      <Button id="back" color="primary" onClick={this.goBack}>
                        Back to{' '}
                        {getObjVal(history, 'location.state.from', 'Previous')}{' '}
                        View
                      </Button>
                    )}
                  </CardFooter>
                </Card>
              )
            ) : (
              <div>
                <p>This message will create a new ticket</p>

                <MessageForm
                  message={message}
                  onChange={this.onMessageFormChanged}
                />

                <div className="text-right mt-3">
                  <SendTicketButton
                    sending={sending}
                    attaching={uploadInProgress}
                    onClick={this.onSend}
                  >
                    Send
                  </SendTicketButton>
                </div>
              </div>
            )}
          </Panel>
          <Panel className="cse w-100 mb-prs">
            <div>
              <img alt="Arctic Wolf Avatar" src="/wolf.png" />
            </div>
            {contactAWN}
            <hr />
            <h5 className="mb-1">Email</h5>
            <address>
              {showOnboarding ? (
                <a href="mailto:onboarding@arcticwolf.com">
                  onboarding@arcticwolf.com
                </a>
              ) : (
                <a href="mailto:security@arcticwolf.com">
                  security@arcticwolf.com
                </a>
              )}
            </address>
            <h5 className="mb-1">North America (NA)</h5>
            <span data-cy="contact-number">
              1 888-272-8429, option {showOnboarding ? '3' : '2'}
            </span>
            <h5 className="mb-1">Germany (DE)</h5>
            <span data-cy="contact-number">49 30 16639878</span>
            <h5 className="mb-1">Denmark (DK)</h5>
            <span data-cy="contact-number">45 80 82 08 52</span>
            <h5 className="mb-1">United Kingdom (UK)</h5>
            <span data-cy="contact-number">44 800 260 6438</span>
          </Panel>
        </div>
      </Viewport>
    );
  }
}

export default connect<StoreProps, DispatchProps, OwnProps>(
  (store): StoreProps => ({
    cst: sessionSelector.getCST(store),
    onboardingTeamContacts: onboardingSelector.getOnboardingTeamContacts(store),
  }),
  (dispatch: Dispatch): DispatchProps => ({
    setTicketFilters: (filter: URLSearchParams) =>
      dispatch(TICKET_ACTIONS.SET_FILTERS(filter)),
    onCreateTicket: (
      message: MessageBody,
      showOnboarding?: boolean,
      assigneeEmail?: string,
      emailCCs?: string[],
      onboardingForm?: boolean,
      pmTag?: string,
      pseTag?: string,
    ) =>
      dispatch(
        apiRequest.createTicket(
          message.subject,
          message.description,
          message.attachmentTokens,
          showOnboarding ? ONBOARDING_DISPATCH_TICKET_TYPE : '',
          showOnboarding ? getOrigin() : undefined,
          assigneeEmail,
          emailCCs,
          (onboardingForm = showOnboarding), //we set onboarding_flag true for only contact onboarding
          pmTag,
          pseTag,
        ),
      ),
    fetchOnboardingTeamContacts: () =>
      dispatch(apiRequest.getOnboardingTeamContacts()),
  }),
)(makeComponentTrashable(BareContact));
